import { ImagePullPolicy } from './image-options/useImageOptionsStore';

/** 工作负载的组件用于什么领域 */
export enum WorkloadType {
  /** 部署 */
  DEPLOYMENT = 'deployment',
  /** 有状态副本集 */
  STATEFULSET = 'statefulset',
  /** 守护进程 */
  DAEMONSET = 'daemonset',
}

/**
 * 工作负载业务相关组件的根接口
 * 用于标识组件服务工作负载的什么业务,默认应该是 DEPLOYMENT
 * @example
 * interface YourComponentProps extends WorkloadComponent { ... }
 */
export interface WorkloadComponent {
  /** 标识组件服务什么业务 */
  module: WorkloadType;
}

/** 容器端口设置 */
export interface ContainerPort {
  /** 端口名称 */
  name?: string;
  /** 协议 */
  protocol?: string;
  /** 容器端口 */
  containerPort?: number;
  /** statefulset 服务端口  */
  servicePort?: number;
}

/** statefulset 无头服务的端口 */
export interface HeadlessServicePort {
  name?: string;
  protocol?: string;
  port?: number; // 对应容器端口中的 servicePort
  targetPort?: number; // 对应容器端口的 containerPort
}

/** 声明周期管理操作项 */
export type LifecycleItem =
  | {
      httpGet?: {
        scheme?: string;
        path?: string;
        port?: number;
      };
    }
  | {
      exec?: {
        command: string[];
      };
    }
  | {
      tcpSocket?: {
        port: number;
      };
    };

/** 健康检查公共部分 */
interface HealthCheckCommon {
  initialDelaySeconds?: number;
  timeoutSeconds?: number;
  periodSeconds?: number;
  successThreshold?: number;
  failureThreshold?: number;
}
export type Probe = LifecycleItem & HealthCheckCommon;

/** 转换成 yaml 的环境变量数据结构 */
export type YamlEnvItem =
  /** 自定义 */
  | {
      name: string;
      value?: string;
    }
  /** 来自配置字典 */
  | {
      name: string;
      valueFrom: {
        configMapKeyRef?: {
          name?: string;
          key?: string;
        };
      };
    }
  /** 来自密保字典 */
  | {
      name: string;
      valueFrom: {
        secretKeyRef?: {
          name?: string;
          key?: string;
        };
      };
    };

/** 调度亲和力 */
export interface AffinityItem {
  weight?: number; // 1
  podAffinityTerm?: {
    labelSelector?: {
      matchLabels?: {
        app?: string; // 关联 app 名字
      };
    };
    topologyKey?: string;
  };
}

/** 容器挂载卷 */
export interface YamlContainerVolumeMount {
  name?: string;
  mountPath?: string;
  readOnly?: boolean;
}
/**
 * 收集的容器数据
 * 容器类型就是把这个数据结构放到 spec.containers 或者 spec.initContainers 数组中
 */
export interface ContainerData {
  name?: string;
  ports?: ContainerPort[];
  image?: string; // 镜像
  resources?: {
    requests?: {
      cpu?: number | string; // '11'
      memory?: string; // 33Mi
      [key: string]: number | string;
      // ['nvidia.com/gpu']?: number; // '44'
    };
    limits?: {
      cpu?: number | string; // '11'
      memory?: string; // 33Mi
      [key: string]: number | string;
      // ['nvidia.com/gpu']?: number; // '44'
    };
  };
  /** 镜像拉取策略 */
  imagePullPolicy?: ImagePullPolicy;
  /** 健康检查 - 存活检查 */
  livenessProbe?: Probe;
  /** 健康检查 - 就绪检查 */
  readinessProbe?: Probe;
  /** 健康检查 - 启动检查 */
  startupProbe?: Probe;
  /** 声明周期管理 */
  lifecycle?: {
    /** 启动后动作 */
    postStart?: LifecycleItem;
    /** 终止前动作 */
    preStop?: LifecycleItem;
  };
  /** 启动命令 - 命令 */
  command?: string[];
  /** 启动命令 - 参数 */
  args?: string[];
  /** tty */
  tty?: boolean;
  /** 环境变量 */
  env?: YamlEnvItem[];
  /** 容器安全上下文 */
  securityContext?: {
    privileged?: boolean; // 特权模式
    allowPrivilegeEscalation?: boolean; // 允许特权提升
    readOnlyRootFilesystem?: boolean; // 根目录只读
    runAsNonRoot?: boolean; // 非 root 用户运行
    runAsUser?: number; // 用户 ID
    runAsGroup?: number; // 用户组 ID
    seLinuxOptions?: {
      level?: string; // 等级
      role?: string; // 角色
      type?: string; // 类型
      user?: string; // 用户
    };
    capabilities?: {
      add?: string[]; // 权能 - 添加
      drop?: string[]; // 权能 - 移除
    };
  };
  volumeMounts?: YamlContainerVolumeMount[];
}

/** 存储 - 持久卷 */
export interface YamlVolumePersistenceItem {
  name: string;
  persistentVolumeClaim: {
    claimName: string;
  };
}
/** 存储 - 临时卷 */
export interface YamlVolumeTempItem {
  name: string;
  emptyDir: {};
}

/** 存储 - HostPath卷 */
export interface YamlVolumeHostPathItem {
  name: string;
  hostPath: {
    path: string;
  };
}

/** 存储 - 特定的键 */
interface SpecificKeyItem {
  key: string;
  path: string;
}

/** 存储 - 配置字典 */
export interface YamlVolumeConfigMapItem {
  name: string;
  configMap: {
    name: string;
    items?: SpecificKeyItem[];
  };
}
/** 存储 - 保密字典 */
export interface YamlVolumeSecretItem {
  name: string;
  secret: {
    secretName: string;
    items?: SpecificKeyItem[];
  };
}

/** 存储添加的列表 */
export type YamlVolumeMountItem =
  | YamlVolumePersistenceItem
  | YamlVolumeTempItem
  | YamlVolumeHostPathItem
  | YamlVolumeConfigMapItem
  | YamlVolumeSecretItem;

/** 亲和性 */
export interface YamlAffinityItem {
  /** 默认: 空对象 */
  /** 分散调度 */
  podAntiAffinity?: {
    preferredDuringSchedulingIgnoredDuringExecution?: AffinityItem[];
  };
  /** 集中调度 */
  podAffinity?: {
    preferredDuringSchedulingIgnoredDuringExecution?: AffinityItem[];
  };
  /** 自定义规则
   * 需要关联这两个调度的规则, 细分规则需要咨询后端
   */
}

/**
 * 更新策略
 */
export interface YamlUpdateStrategy {
  type?: string;
  rollingUpdate?: {
    maxUnavailable?: string; // deployment | daemonset
    maxSurge?: string; // deployment
    partition?: number; // statefulset only
  };
}

/** 持久卷声明模板 */
export interface YamlVolumeClaimTemplate {
  metadata?: {
    name?: string;
    namespace?: string;
  };
  spec?: {
    accessModes?: string[]; // 访问模式 eg: ReadWriteOnce
    resources?: {
      requests?: {
        storage?: string; // 存储大小 eg: 429Gi
      };
    };
    storageClassName?: string; // 存储类名称
  };
}

// yaml 收集的数据结构
export interface WorkloadYaml {
  apiVersion?: 'apps/v1';
  kind?: string;
  metadata?: {
    name?: string; // 名称
    namespace?: string; // 命名空间
    labels?: {
      app?: string; // 这里的键值对, 默认给高级设置里面的添加元数据
      ['stack.io/creator']?: string; // 创建者uuid
      ['stack.io/creatorName']?: string; // 创建者名称
      [key: string]: string;
    };
    annotations?: {
      ['stack.io/aliase']?: string; // 别名
      ['stack.io/describe']?: string; // 描述
      [key: string]: string; // 高级设置 - 添加元数据 - 注解
    };
  };
  spec: {
    replicas?: number;
    selector?: {
      matchLabels: {
        app?: string;
      };
    };
    template?: {
      metadata?: {
        labels?: {
          app?: string;
        };
        /** 添加元数据 */
        annotations?: Record<string, string>;
      };
      spec?: {
        serviceAccount?: string;
        containers?: ContainerData[];
        initContainers?: ContainerData[];
        /** 同步主机时区需要给 volumes 添加一个预设的卷, 查看挂载卷相关的目录 */
        volumes?: YamlVolumeMountItem[];
        // imagePullSecrets?: Array<{}>;
        securityContext?: {
          /** 仅允许非 root 用户运行 */
          runAsNonRoot?: boolean;
          /** 用户 ID */
          runAsUser?: number;
          /** 用户组 ID */
          runAsGroup?: number;
          /** SELinux 上下文 */
          seLinuxOptions?: {
            level?: string; // 等级
            role?: string; // 角色
            type?: string; // 类型
            user?: string; // 用户
          };
        };
        /** 容器组调度规则 */
        affinity?: YamlAffinityItem;
        /** 容器组调度规则 - 自定义规则 */
        customMode?: {
          policy?: string;
          type?: string;
          target?: string;
        }[];
        /** 高级设置 - 指定节点 */
        nodeSelector?: Record<string, string>;
      };
    };
    strategy?: YamlUpdateStrategy;
    /* ============================ statefulset ==================================== */
    serviceName?: string; // statefulset 必要的服务名称
    updateStrategy?: YamlUpdateStrategy; // statefulset | daemonset 更新策略
    volumeClaimTemplates?: YamlVolumeClaimTemplate[];
    /** ============================ daemonset ==================================== */
    minReadySeconds?: number; // daemonset 最小就绪时间
  };
}

// statefulset 必须的无头服务收集的数据结构
export interface HeadlessServiceYaml {
  apiVersion?: 'v1';
  kind?: 'Service';
  metadata?: {
    labels?: {
      app?: string; // 关联 app 名字
      ['stack.io/creator']?: string; // 创建者uuid
      ['stack.io/creatorName']?: string; // 创建者名称
      [key: string]: string;
    };
    name?: string; // 生成的服务名称, 且与上面 yaml 中的 serviceName 一致
    annotations?: {
      ['stack.io/aliase']?: string; // 关联别名
      ['stack.io/statefulset']?: string; // 关联 app 名字
    };
  };
  spec?: {
    clusterIP: 'None';
    sessionAffinity: 'None';
    selector?: {
      app?: string; // 关联 app 名字
    };
    ports?: {
      // 上面容器端口映射, statefulset 有 targetPort
      name?: string;
      protocol?: string;
      port?: number; // 对应容器端口中的 servicePort
      targetPort?: number; // 对应容器端口的 containerPort
    }[];
  };
}
